const FONT_FACE_URL_REGEX = /url\(['"]?([^'")]+)['"]?\)/g;

// If we are embedding inside our platform or matrix app
const isUAHostApp = () => Boolean(document.querySelector('meta[name="ua-app-type"]'));

const isUAThirdPartyLib = (stylesheet: HTMLLinkElement): boolean => {
  const href = stylesheet.getAttribute('href') ?? '';

  // If stylesheet has ua-dm-css then its delta-matrix stylesheet and should not be treated as third party lib css
  if (stylesheet.hasAttribute('ua-dm-css')) {
    return false;
  }

  try {
    const url = new URL(href);
    // If stylesheet is third party lib from *.unifyapps.com/lib. for eg. monaco, pdfjs, etc
    return url.hostname.endsWith('unifyapps.com') && url.pathname.startsWith('/lib/');
  } catch {
    return false;
  }
};

/* 
  1. <ua-dm> web component to wrap element in shadow DOM, so that host styles don't affect matrix app
  2. Insert all stylesheets with attribute ua-dm-css into shadow DOM
  3. Sets remMultiplier css variable in shadow DOM so that matrix app styles are adjusted according to host app's base rem value
 */
export class MatrixWebComponent extends HTMLElement {
  private stylesheetsObserver: MutationObserver | null = null;
  private shadowHead: HTMLDivElement;
  private isUAHostApp: boolean;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    // Create a div to act as a pseudo head
    this.shadowHead = document.createElement('div');
    this.shadowHead.setAttribute('ua-dm-head', '');
    this.shadowRoot?.appendChild(this.shadowHead);

    this.isUAHostApp = isUAHostApp();
  }

  connectedCallback() {
    // Insert existing stylesheets into shadow DOM
    const stylesheets = document.querySelectorAll('link[rel="stylesheet"]');
    this.processStylesheets(stylesheets);

    // Observe dynamically added stylesheets
    this.observeStylesheets();
  }

  disconnectedCallback() {
    this.stylesheetsObserver?.disconnect();
  }

  /**
   * Processes and inserts stylesheets into the shadow DOM.
   */
  private processStylesheets(stylesheets: NodeListOf<Element> | HTMLLinkElement[]) {
    stylesheets.forEach((stylesheet) => {
      const href = stylesheet.getAttribute('href') ?? '';

      if (stylesheet.hasAttribute('ua-dm-css') || isUAThirdPartyLib(stylesheet)) {
        // Handle font-face extraction per stylesheet
        if (stylesheet instanceof HTMLLinkElement) {
          // For already loaded stylesheets
          if (stylesheet.sheet) {
            this.extractFontFaces(stylesheet.sheet, href);
          }
          // For stylesheets that are still loading
          else {
            stylesheet.addEventListener('load', () => {
              this.extractFontFaces(stylesheet.sheet, href);
            });
          }
        }

        if (isUAThirdPartyLib(stylesheet) && this.isUAHostApp) {
          // If host app is a UnifyApps app and the stylesheet is third party lib. We need to clone it
          // as this stylesheet won't have ua-dm-css attribute, and we don't want to move it as that will remove it from host app
          this.shadowHead.appendChild(stylesheet.cloneNode(true));
        } else {
          // Otherwise, stylsheet is delta-matrix specific and we can remove it from the host app
          // and move to shadow DOM
          this.shadowHead.appendChild(stylesheet);
        }
      }
    });
  }

  /**
   * Extracts @font-face rules from a stylesheet and adds them the document head.
   * As browser does not load font's defined inside shadow dom
   */
  private extractFontFaces(sheet: CSSStyleSheet | null, href: string): void {
    if (!sheet) return;

    try {
      // Get the base URL from the stylesheet href
      // for eg. if our css file is at https://domain/path/web.css
      // then we want our font's to be loaded from https://domain/path/{fontUrl}
      // since url in font-face rule will be relative to css file, and here we are moving
      // font-face rules to head in style tags, so we need to make font urls absolute
      const baseUrl = new URL('.', new URL(href, window.location.href)).href;

      const cssRules = Array.from(sheet.cssRules || []);
      let fontFaceRules = cssRules
        .filter((rule) => rule instanceof CSSFontFaceRule)
        .map((rule) => rule.cssText);

      // Rewrite relative URLs to absolute
      fontFaceRules = fontFaceRules.map((rule) => {
        // Replace url('./path') or url("./path") or url(./path) with absolute URLs
        return rule.replace(FONT_FACE_URL_REGEX, (match, url) => {
          if (url.startsWith('/') || url.startsWith('http')) {
            // Already absolute
            return `url('${url}')`;
          }
          // Convert relative to absolute
          return `url('${baseUrl}${url}')`;
        });
      });

      if (fontFaceRules.length > 0) {
        // Create a new style element for this stylesheet's font-face rules
        const fontStyle = document.createElement('style');
        fontStyle.setAttribute('data-ua-font-faces', href);
        fontStyle.textContent = fontFaceRules.join('\n');
        document.head.appendChild(fontStyle);
      }
    } catch (e) {
      console.warn('UnifyApps: Cannot access stylesheet rules', e);
    }
  }

  /**
   * Observes the <head> element for dynamically added stylesheets.
   */
  private observeStylesheets() {
    const head = document.head;

    this.stylesheetsObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          const addedStylesheets = Array.from(mutation.addedNodes).filter(
            (node): node is HTMLLinkElement =>
              node instanceof HTMLLinkElement && node.rel === 'stylesheet',
          );
          this.processStylesheets(addedStylesheets);
        }
      });
    });

    this.stylesheetsObserver.observe(head, { childList: true });
  }

  insertElement(el: HTMLElement) {
    // wrap the element in shadow-dom
    el.parentNode?.insertBefore(this, el);
    this.appendChild(el);

    // move el inside shadow root
    this.shadowRoot?.appendChild(el);

    // insert remMultiplier css variable
    const defaultBaseRem = this.getDefaultBaseRem();
    const currentDocRem = parseFloat(window.getComputedStyle(document.documentElement).fontSize);
    this.insertCssVariable('--remMultiplier', defaultBaseRem / currentDocRem);
  }

  insertCssVariable(variableName: string, value: string | number) {
    const style = document.createElement('style');
    style.textContent = `:host { ${variableName}: ${value}; }`;
    this.shadowHead.appendChild(style);
  }

  getDefaultBaseRem() {
    let defaultBaseRem = 16;
    try {
      // Temporary add iframe to get default base rem value
      const iframe = document.createElement('iframe');
      this.shadowRoot?.appendChild(iframe);
      defaultBaseRem = parseFloat(
        window.getComputedStyle(iframe.contentWindow!.document.documentElement).fontSize,
      );
      this.shadowRoot?.removeChild(iframe);
    } catch (err) {
      console.log('Failed to get default base rem value: ', err);
    }

    return defaultBaseRem;
  }

  getShadowHead(): HTMLDivElement {
    return this.shadowHead;
  }
}

window.customElements.define('ua-dm', MatrixWebComponent);
